import { BaseApi } from "~/services/baseApi";
import { API_URL_MEDIA } from "~/constants/url";
import moment from "moment";
import { JsonResponseType } from "~/types/jsonResponseType";

const mediaRequest = BaseApi({ baseUrl: API_URL_MEDIA });

const REQUEST_THUMBNAIL_COUNT = 20;

const imageUrlCache: {
  [key: string]: { s3Key: string; time: moment.Moment };
} = {};

const getFilesWithTimeout: {
  [key: string]: { s3Key: string; listener: (path: string) => void }[];
} = {};

type KeyPathPair = {
  key: string;
  path: string;
};

export const getFilePath = async (s3Key: string, noCache: boolean = false) => {
  if (!noCache) {
    const cacheSrc = imageUrlCache[s3Key];
    if (cacheSrc && cacheSrc.time.diff(moment(), "minutes") < 5) {
      return cacheSrc.s3Key;
    }
  }

  const timeoutObjects = getFilesWithTimeout;

  const timeoutSeconds = 1010;

  const timeoutKey = moment().format("mm:ss");

  if (!timeoutObjects[timeoutKey]) {
    timeoutObjects[timeoutKey] = [];

    const runSetTimeout = (length: number = 0) => {
      setTimeout(async () => {
        const srcListeners = timeoutObjects[timeoutKey]
          .filter((x) => x.s3Key)
          .slice(
            length,
            timeoutObjects[timeoutKey].length > length + REQUEST_THUMBNAIL_COUNT
              ? length + REQUEST_THUMBNAIL_COUNT
              : timeoutObjects[timeoutKey].length,
          );

        if (srcListeners.length > 0) {
          const uniqueSrcList = [...new Set(srcListeners.map((x) => x.s3Key))];

          const pathList = await mediaRequest<KeyPathPair[], KeyPathPair[]>({
            action: "get-all-paths",
            method: "POST",
            body: JSON.stringify(uniqueSrcList),
            responseMapper: (
              input: KeyPathPair[],
            ): JsonResponseType<KeyPathPair[]> => {
              return {
                succeed: input.some((x) => x.path),
                data: input,
                messageCode: "",
                rowCount: input.length,
              };
            },
          });

          srcListeners.forEach((srcListener) => {
            srcListener.listener(
              pathList.find((x) => x.key === srcListener.s3Key)?.path ?? "",
            );
          });
        }

        if (srcListeners.length < REQUEST_THUMBNAIL_COUNT) {
          delete timeoutObjects[timeoutKey];
        } else {
          runSetTimeout(length + REQUEST_THUMBNAIL_COUNT);
        }
      }, timeoutSeconds);
    };

    runSetTimeout();
  }

  return new Promise<string>((resolve, reject) => {
    timeoutObjects[timeoutKey].push({
      s3Key,
      listener: (path) => {
        if (path) {
          imageUrlCache[s3Key] = { s3Key: path, time: moment() };
          resolve(path);
        } else {
          reject(new Error("path is empty!"));
        }
      },
    });
  });
};

export const getFilePaths = async (s3Keys: string[], explicitToken: string) => {
  const response = await mediaRequest({
    action: "get-all-paths",
    method: "POST",
    body: JSON.stringify(s3Keys),
    headers: {
      Authorization: `Bearer ${explicitToken}`,
    },
    responseMapper: (input: KeyPathPair[]): JsonResponseType<KeyPathPair[]> => {
      return {
        succeed: input.some((x) => x.path),
        data: input,
        messageCode: "",
        rowCount: input.length,
      };
    },
  });

  return response;
};
